// Per-service page data for SEO landing pages.
// Each service produces a dedicated /[slug] route with its own H1, content,
// FAQ, and JSON-LD Service schema.

export const services = [
  {
    slug: 'iphone-repair',
    name: 'iPhone Repair',
    h1: 'iPhone Repair in Round Lake Beach, IL',
    tagline: 'Cracked screen? Dead battery? We fix every iPhone — same day.',
    description:
      'From iPhone 6 all the way to the latest iPhone 16 Pro Max, our OEM-certified technicians repair Apple devices fast, with industry-leading warranty. Walk in or get an instant quote online.',
    icon: 'Smartphone',
    issues: [
      'Cracked screen / LCD replacement',
      'Battery replacement',
      'Charging port repair',
      'Back glass replacement',
      'Camera lens & rear camera fixes',
      'Water damage diagnostics',
      'Speaker, microphone & Face ID issues',
    ],
    startingPrice: 'from $79',
    faq: [
      {
        question: 'How much does an iPhone screen repair cost?',
        answer:
          'iPhone screen repairs at Nexera start from $79 depending on the model. We provide a free, no-obligation quote before any work begins. Lease-to-own financing is available through Acima Leasing.',
      },
      {
        question: 'How long does an iPhone repair take?',
        answer:
          'Most iPhone screen and battery repairs are completed in 30–90 minutes. Same-day turnaround is standard for in-stock parts.',
      },
      {
        question: 'Do you use genuine Apple parts?',
        answer:
          'We use genuine OEM and high-quality parts that meet Apple specifications. Every iPhone repair is covered by our industry-leading warranty.',
      },
      {
        question: 'Will Face ID still work after a screen repair?',
        answer:
          'Yes. Our technicians transfer the original sensors and follow the proper procedures so Face ID remains fully functional after a screen replacement.',
      },
    ],
  },
  {
    slug: 'samsung-repair',
    name: 'Samsung Repair',
    h1: 'Samsung Galaxy Repair in Round Lake Beach, IL',
    tagline: 'Galaxy S, Note, Z Flip, Z Fold — repaired by certified techs.',
    description:
      'Whether you cracked your Galaxy S24 Ultra screen or your Z Fold hinge is failing, we repair every modern Samsung device. Same-day service on most repairs.',
    icon: 'Smartphone',
    issues: [
      'AMOLED screen replacement',
      'Back glass repair',
      'Battery replacement',
      'Charging port / USB-C repair',
      'Camera & fingerprint sensor fixes',
      'Folding hinge & flex cable repair',
      'Water damage restoration',
    ],
    startingPrice: 'from $89',
    faq: [
      {
        question: 'How much does a Samsung screen repair cost?',
        answer:
          'Samsung Galaxy screen repairs start from $89 and vary by model. Foldables (Z Flip / Z Fold) are quoted individually. Acima Leasing financing is available.',
      },
      {
        question: 'Do you fix Galaxy Z Fold and Z Flip phones?',
        answer:
          'Yes — we repair Samsung foldable devices including inner displays, outer screens, and hinge mechanisms.',
      },
      {
        question: 'How long does a Samsung battery replacement take?',
        answer:
          'Most Galaxy battery replacements are completed in under 2 hours. Walk-ins welcome.',
      },
    ],
  },
  {
    slug: 'laptop-repair',
    name: 'Laptop Repair',
    h1: 'Laptop & Computer Repair in Round Lake Beach, IL',
    tagline: 'Mac, PC, Chromebook — slow, broken, or won\'t boot? We fix it.',
    description:
      'From hardware swaps to virus removal and SSD upgrades, we restore your laptop or desktop to like-new performance. Free diagnostics for all repairs.',
    icon: 'Laptop',
    issues: [
      'Screen / LCD replacement',
      'Keyboard & trackpad repair',
      'Battery replacement',
      'Hard drive → SSD upgrades',
      'RAM upgrades',
      'Virus & malware removal',
      'Liquid damage recovery',
      'Data backup & recovery',
      'OS reinstall & tune-up',
    ],
    startingPrice: 'Diagnostics FREE',
    faq: [
      {
        question: 'Do you repair Apple MacBooks?',
        answer:
          'Yes — we service MacBook Air, MacBook Pro (Intel and Apple Silicon), iMac, and Mac mini. Screen, battery, keyboard, and logic-board level diagnostics included.',
      },
      {
        question: 'My laptop is slow. Can you fix it without a full reset?',
        answer:
          'In most cases, yes. A combination of malware removal, SSD upgrade, and OS optimization brings most older laptops back to like-new speed without losing your data.',
      },
      {
        question: 'How much does laptop repair cost?',
        answer:
          'Diagnostics are FREE. Repair pricing depends on the issue — we give a written quote before any work is done.',
      },
    ],
  },
  {
    slug: 'tablet-repair',
    name: 'Tablet Repair',
    h1: 'iPad & Tablet Repair in Round Lake Beach, IL',
    tagline: 'iPad, Galaxy Tab, Surface, Fire — cracked screens fixed fast.',
    description:
      'We repair every major tablet brand. Screen, digitizer, battery, and charging port — all backed by our industry-leading warranty.',
    icon: 'Tablet',
    issues: [
      'iPad screen / digitizer replacement',
      'Battery replacement',
      'Home / power button repair',
      'Charging port repair',
      'Software & restore issues',
      'Microsoft Surface repair',
    ],
    startingPrice: 'from $99',
    faq: [
      {
        question: 'Do you fix iPad Pro and iPad Air?',
        answer:
          'Yes — all iPad models from iPad 2 through the latest iPad Pro M4 are supported.',
      },
      {
        question: 'How long does an iPad screen replacement take?',
        answer:
          'Most iPad screen replacements take 24–48 hours due to adhesive cure time. We will give you an exact ETA at drop-off.',
      },
    ],
  },
  {
    slug: 'gaming-console-repair',
    name: 'Gaming Console Repair',
    h1: 'Gaming Console Repair in Round Lake Beach, IL',
    tagline: 'PlayStation, Xbox, Nintendo Switch — back in the game fast.',
    description:
      'HDMI port broken? Disc drive failing? Switch joycons drifting? Our team repairs every modern console with same-week turnaround.',
    icon: 'Gamepad2',
    issues: [
      'HDMI port replacement (PS5, PS4, Xbox)',
      'Disc drive repair',
      'Overheating / fan cleaning',
      'Switch joycon drift repair',
      'Controller repair',
      'Storage upgrades',
    ],
    startingPrice: 'from $79',
    faq: [
      {
        question: 'Do you fix PS5 HDMI ports?',
        answer:
          'Yes — HDMI port replacement is one of our most common console repairs. Most are turned around in 3–5 business days.',
      },
      {
        question: 'Can you fix Nintendo Switch joycon drift?',
        answer:
          'Absolutely. We replace joystick modules so your joycons feel brand new.',
      },
    ],
  },
  {
    slug: 'water-damage-repair',
    name: 'Water Damage Repair',
    h1: 'Water Damage Phone Repair in Round Lake Beach, IL',
    tagline: 'Dropped your phone in water? Act fast — we can save it.',
    description:
      'Liquid damage is time-sensitive. Bring your device to Nexera ASAP for professional cleaning, drying, and component-level restoration. The sooner the better — power off your device and bring it in immediately.',
    icon: 'Droplets',
    issues: [
      'Component-level cleaning',
      'Logic board restoration',
      'Battery & port replacement',
      'Data recovery from damaged devices',
    ],
    startingPrice: 'Diagnostics FREE',
    faq: [
      {
        question: 'Can a phone with water damage be saved?',
        answer:
          "Often yes — especially if you bring it in quickly and don't try to charge it. We've successfully restored thousands of liquid-damaged devices.",
      },
      {
        question: 'How long does water damage repair take?',
        answer:
          'Diagnostics take 24 hours. Repairs depend on what components need replacement — we will give you a full report and quote before any work.',
      },
      {
        question: 'What should I do if I just dropped my phone in water?',
        answer:
          "1) Power it off immediately. 2) Don't charge it. 3) Don't put it in rice. 4) Bring it to us ASAP — time is critical.",
      },
    ],
  },
];

export const getServiceBySlug = (slug) => services.find((s) => s.slug === slug);
